import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../../ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../ui/select';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText, 
  Download, 
  Save,
  X,
  Package,
  DollarSign,
  Building,
  User,
  Mail,
  Phone
} from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';
import { pdf } from '@react-pdf/renderer';
import { Packer } from 'docx';
import OfferPDFDocument from '../OfferPDFDocument';
import { ensureRobotoFontForPdf } from '../../../utils/pdfFonts';
import { buildOfferDocx } from '../../../utils/offerDocx';

const AdminOfferRequests = () => {
  const VAT_RATE = 0.20; // %20 KDV
  const [offers, setOffers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [footerSettings, setFooterSettings] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showServiceSelect, setShowServiceSelect] = useState(false);
  const [showPackageSelect, setShowPackageSelect] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    offer_title: '',
    offer_data: {
      services: [],
      custom_packages: [],
      notes: ''
    },
    business_id: '',
    business_snapshot: null,
    total_amount: 0,
    notes: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchOffers();
    fetchPackages();
    fetchServices();
    fetchBusinesses();
    fetchCompanySettings();
    fetchFooterSettings();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getBusinesses`);
      const data = await response.json();
      if (data?.status === 'success' && Array.isArray(data.data)) {
        setBusinesses(data.data);
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const handleBusinessSelect = (value) => {
    if (value === '__clear__') {
      setFormData((prev) => ({
        ...prev,
        business_id: '',
        business_snapshot: null
      }));
      return;
    }
    const selected = businesses.find((b) => String(b.id) === String(value));
    setFormData((prev) => ({
      ...prev,
      business_id: value,
      business_snapshot: selected ? { ...selected } : null,
      company_name: selected?.title || prev.company_name,
      contact_name: selected?.contact_name || prev.contact_name,
      email: selected?.email || prev.email,
      phone: selected?.phone || prev.phone,
      address: selected?.address ?? prev.address,
      tax_number: selected?.tax_number ?? prev.tax_number
    }));
  };

  const numberFmt = (n) =>
    (Number.isFinite(parseFloat(n)) ? parseFloat(n) : 0).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const computeTotals = (state = formData) => {
    const servicesList = Array.isArray(state.offer_data?.services) ? state.offer_data.services : [];
    const packagesList = Array.isArray(state.offer_data?.custom_packages) ? state.offer_data.custom_packages : [];

    const servicesTotal = servicesList.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
    const packagesTotal = packagesList.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);

    const subtotal = servicesTotal + packagesTotal;
    const vatAmount = subtotal * VAT_RATE;
    const total = subtotal + vatAmount;

    return { subtotal, vatAmount, total };
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.GET_COMPANY_SETTINGS);
      const data = await response.json();
      if (data.status === 'success' && data.data) {
        setCompanySettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchFooterSettings = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.GET_FOOTER_SETTINGS);
      const data = await response.json();
      if (data.status === 'success' && data.data) {
        setFooterSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching footer settings:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.OFFER_REQUESTS);
      const data = await response.json();
      if (data.status === 'success') {
        setOffers(data.offers || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.GET_ALL_PACKAGES_FOR_OFFER);
      const data = await response.json();
      if (data.status === 'success') {
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.SERVICES);
      const data = await response.json();
      if (data.status === 'success') {
        // Backend 'data' key'i ile dönüyor, 'services' değil
        const servicesList = data.data || data.services || [];
        // Services'i teklif formatına uygun hale getir
        const formattedServices = servicesList.map(service => ({
          id: service.id,
          name: service.title || service.name || '',
          description: service.description || service.detailed_description || '',
          price: parseFloat(service.price) || 0
        }));
        setServices(formattedServices);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const openModal = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      // offer_data'yı güvenli şekilde parse et
      const offerData = offer.offer_data || {};
      setFormData({
        company_name: offer.company_name || '',
        contact_name: offer.contact_name || '',
        email: offer.email || '',
        phone: offer.phone || '',
        address: offer.address || '',
        tax_number: offer.tax_number || '',
        offer_title: offer.offer_title || '',
        offer_data: {
          services: Array.isArray(offerData.services) ? offerData.services : [],
          custom_packages: Array.isArray(offerData.custom_packages) ? offerData.custom_packages : [],
          notes: offerData.notes || ''
        },
        business_id: offer.business_id || '',
        business_snapshot: offer.business_snapshot || null,
        total_amount: parseFloat(offer.total_amount) || 0,
        notes: offer.notes || '',
        status: offer.status || 'draft'
      });
    } else {
      setEditingOffer(null);
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        tax_number: '',
        offer_title: '',
        offer_data: {
          services: [],
          custom_packages: [],
          notes: ''
        },
        business_id: '',
        business_snapshot: null,
        total_amount: 0,
        notes: '',
        status: 'draft'
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOffer(null);
  };

  const addService = () => {
    const newService = {
      id: Date.now(),
      name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      total: 0,
      original_service_id: null
    };
    setFormData(prev => ({
      ...prev,
      offer_data: {
        ...prev.offer_data,
        services: [...prev.offer_data.services, newService]
      }
    }));
    calculateTotal();
  };

  const addServiceFromDatabase = (serviceId) => {
    const service = services.find(s => String(s.id) === String(serviceId));
    if (!service) return;

    const newService = {
      id: Date.now(),
      name: service.name || '',
      description: service.description || '',
      quantity: 1,
      unit_price: parseFloat(service.price) || 0,
      total: parseFloat(service.price) || 0,
      original_service_id: service.id
    };
    
    setFormData(prev => {
      const updatedServices = [...prev.offer_data.services, newService];
      const totalAmount = updatedServices.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) +
        prev.offer_data.custom_packages.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
      
      return {
        ...prev,
        offer_data: {
          ...prev.offer_data,
          services: updatedServices
        },
        total_amount: totalAmount
      };
    });
    
    setShowServiceSelect(false);
    calculateTotal();
  };

  const updateService = (id, field, value) => {
    setFormData(prev => {
      const servicesList = Array.isArray(prev.offer_data?.services) ? prev.offer_data.services : [];
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      
      const services = servicesList.map(service => {
        if (service.id === id) {
          const updated = { ...service, [field]: value };
          if (field === 'quantity' || field === 'unit_price') {
            updated.total = parseFloat(updated.quantity || 0) * parseFloat(updated.unit_price || 0);
          }
          return updated;
        }
        return service;
      });
      
      const totalAmount = services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) +
        packagesList.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
      
      return {
        ...prev,
        offer_data: { ...prev.offer_data, services },
        total_amount: totalAmount
      };
    });
  };

  const removeService = (id) => {
    setFormData(prev => ({
      ...prev,
      offer_data: {
        ...prev.offer_data,
        services: Array.isArray(prev.offer_data?.services) 
          ? prev.offer_data.services.filter(s => s.id !== id)
          : []
      }
    }));
    calculateTotal();
  };

  const addCustomPackage = () => {
    const newPackage = {
      id: Date.now(),
      name: '',
      description: '',
      price: 0,
      features: [],
      based_on_package_id: null,
      original_package_id: null
    };
    setFormData(prev => ({
      ...prev,
      offer_data: {
        ...prev.offer_data,
        custom_packages: [...prev.offer_data.custom_packages, newPackage]
      }
    }));
  };

  const addPackageFromDatabase = (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    if (!pkg) return;

    // Features'ı array olarak parse et
    let features = [];
    if (pkg.features) {
      if (Array.isArray(pkg.features)) {
        // Eğer zaten array ise, direkt kullan
        features = pkg.features.map(f => {
          // Eğer feature bir object ise, name veya text property'sini al
          if (typeof f === 'object' && f !== null) {
            return f.name || f.text || f.title || JSON.stringify(f);
          }
          return String(f);
        });
      } else if (typeof pkg.features === 'string') {
        try {
          const parsed = JSON.parse(pkg.features);
          if (Array.isArray(parsed)) {
            features = parsed.map(f => {
              if (typeof f === 'object' && f !== null) {
                return f.name || f.text || f.title || JSON.stringify(f);
              }
              return String(f);
            });
          } else {
            features = [String(parsed)];
          }
        } catch {
          // JSON değilse, satır satır böl
          features = pkg.features.split('\n').filter(f => f.trim());
        }
      } else {
        features = [String(pkg.features)];
      }
    }

    const newPackage = {
      id: Date.now(),
      name: pkg.name || '',
      description: pkg.description || '',
      price: parseFloat(pkg.price) || 0,
      features: features,
      based_on_package_id: packageId,
      original_package_id: pkg.original_id,
      package_type: pkg.type
    };
    
    setFormData(prev => {
      const servicesList = Array.isArray(prev.offer_data?.services) ? prev.offer_data.services : [];
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      const updatedPackages = [...packagesList, newPackage];
      const totalAmount = servicesList.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) +
        updatedPackages.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
      
      return {
        ...prev,
        offer_data: {
          ...prev.offer_data,
          custom_packages: updatedPackages
        },
        total_amount: totalAmount
      };
    });
    
    setShowPackageSelect(false);
    calculateTotal();
  };

  const updateCustomPackage = (id, field, value) => {
    setFormData(prev => {
      const servicesList = Array.isArray(prev.offer_data?.services) ? prev.offer_data.services : [];
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      
      const customPackages = packagesList.map(pkg => {
        if (pkg.id === id) {
          return { ...pkg, [field]: value };
        }
        return pkg;
      });
      
      const totalAmount = servicesList.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) +
        customPackages.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
      
      return {
        ...prev,
        offer_data: { ...prev.offer_data, custom_packages: customPackages },
        total_amount: totalAmount
      };
    });
  };

  const addPackageFeature = (packageId) => {
    setFormData(prev => {
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      const customPackages = packagesList.map(pkg => {
        if (pkg.id === packageId) {
          const features = Array.isArray(pkg.features) ? [...pkg.features] : [];
          features.push('');
          return { ...pkg, features };
        }
        return pkg;
      });
      return {
        ...prev,
        offer_data: { ...prev.offer_data, custom_packages: customPackages }
      };
    });
  };

  const updatePackageFeature = (packageId, featureIndex, value) => {
    setFormData(prev => {
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      const customPackages = packagesList.map(pkg => {
        if (pkg.id === packageId) {
          const features = Array.isArray(pkg.features) ? [...pkg.features] : [];
          features[featureIndex] = value;
          return { ...pkg, features };
        }
        return pkg;
      });
      return {
        ...prev,
        offer_data: { ...prev.offer_data, custom_packages: customPackages }
      };
    });
  };

  const removePackageFeature = (packageId, featureIndex) => {
    setFormData(prev => {
      const packagesList = Array.isArray(prev.offer_data?.custom_packages) ? prev.offer_data.custom_packages : [];
      const customPackages = packagesList.map(pkg => {
        if (pkg.id === packageId) {
          const features = Array.isArray(pkg.features) ? [...pkg.features] : [];
          features.splice(featureIndex, 1);
          return { ...pkg, features };
        }
        return pkg;
      });
      return {
        ...prev,
        offer_data: { ...prev.offer_data, custom_packages: customPackages }
      };
    });
  };

  const removeCustomPackage = (id) => {
    setFormData(prev => ({
      ...prev,
      offer_data: {
        ...prev.offer_data,
        custom_packages: Array.isArray(prev.offer_data?.custom_packages)
          ? prev.offer_data.custom_packages.filter(p => p.id !== id)
          : []
      }
    }));
    calculateTotal();
  };

  const calculateTotal = () => {
    const { total } = computeTotals();
    setFormData(prev => ({
      ...prev,
      total_amount: total
    }));
  };

  const handleSave = async () => {
    try {
      const url = editingOffer 
        ? API_URLS.ADMIN.UPDATE_OFFER_REQUEST
        : API_URLS.ADMIN.CREATE_OFFER_REQUEST;
      
      const method = editingOffer ? 'PUT' : 'POST';
      
      const response = await adminApiFetch(url, {
        method,
        body: JSON.stringify({
          ...formData,
          id: editingOffer?.id
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        fetchOffers();
        closeModal();
        alert(editingOffer ? 'Teklif güncellendi' : 'Teklif oluşturuldu');
      } else {
        alert('Hata: ' + (data.message || 'İşlem başarısız'));
      }
    } catch (error) {
      console.error('Error saving offer:', error);
      alert('Teklif kaydedilirken bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu teklifi silmek istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.DELETE_OFFER_REQUEST(id), {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        fetchOffers();
        alert('Teklif silindi');
      } else {
        alert('Hata: ' + (data.message || 'Silme işlemi başarısız'));
      }
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('Teklif silinirken bir hata oluştu');
    }
  };

  const fixTurkishCharsForFilename = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/İ/g, 'I').replace(/ı/g, 'i').replace(/Ş/g, 'S').replace(/ş/g, 's')
      .replace(/Ğ/g, 'G').replace(/ğ/g, 'g').replace(/Ü/g, 'U').replace(/ü/g, 'u')
      .replace(/Ö/g, 'O').replace(/ö/g, 'o').replace(/Ç/g, 'C').replace(/ç/g, 'c');
  };

  const generatePDF = async (offer) => {
    let companyInfo = companySettings;
    let logoUrl = footerSettings?.logo_url;
    if (!companyInfo) {
      try {
        const response = await adminApiFetch(API_URLS.ADMIN.GET_COMPANY_SETTINGS);
        const data = await response.json();
        if (data.status === 'success' && data.data) companyInfo = data.data;
      } catch (e) { console.error(e); }
    }
    if (!logoUrl) {
      try {
        const response = await adminApiFetch(API_URLS.ADMIN.GET_FOOTER_SETTINGS);
        const data = await response.json();
        if (data.status === 'success' && data.data?.logo_url) logoUrl = data.data.logo_url;
      } catch (e) { console.error(e); }
    }
    try {
      await ensureRobotoFontForPdf();
      const blob = await pdf(
        <OfferPDFDocument offer={offer} companyInfo={companyInfo || {}} logoUrl={logoUrl} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Teklif_${fixTurkishCharsForFilename(offer.company_name || 'Teklif').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF oluşturulamadı:', err);
      alert('PDF oluşturulurken bir hata oluştu.');
    }
  };

  const generateDOCX = async (offer) => {
    let companyInfo = companySettings;
    if (!companyInfo) {
      try {
        const response = await adminApiFetch(API_URLS.ADMIN.GET_COMPANY_SETTINGS);
        const data = await response.json();
        if (data.status === 'success' && data.data) companyInfo = data.data;
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const doc = buildOfferDocx(offer, companyInfo || {});
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Teklif_${fixTurkishCharsForFilename(offer.company_name || 'Teklif').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('DOCX oluşturulamadı:', err);
      alert('DOCX oluşturulurken bir hata oluştu.');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-500', text: 'Taslak' },
      sent: { color: 'bg-blue-500', text: 'Gönderildi' },
      accepted: { color: 'bg-green-500', text: 'Kabul Edildi' },
      rejected: { color: 'bg-red-500', text: 'Reddedildi' }
    };
    const cfg = config[status] || config.draft;
    return <Badge className={`${cfg.color} text-white`}>{cfg.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Teklifler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Teklif Oluştur</h1>
          <p className="text-gray-600 mt-1">İşletmelere özel teklifler hazırlayın ve PDF olarak export edin</p>
        </div>
        <Button onClick={() => openModal()} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Teklif
        </Button>
      </div>

      {offers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz teklif yok</h3>
            <p className="text-gray-600">Yeni bir teklif oluşturmak için yukarıdaki butona tıklayın.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {Array.isArray(offers) && offers.map((offer) => (
            <Card key={offer.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {offer.offer_title}
                      </h3>
                      {getStatusBadge(offer.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <Building className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{offer.company_name}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{offer.contact_name}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{offer.email}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600 font-semibold">
                          {parseFloat(offer.total_amount).toFixed(2)} ₺
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateDOCX(offer)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      DOCX İndir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generatePDF(offer)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF İndir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openModal(offer)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(offer.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="p-0 sm:p-0">
            <DialogTitle className="text-base sm:text-lg">
              {editingOffer ? 'Teklif Düzenle' : 'Yeni Teklif Oluştur'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Müşteri bilgilerini girin ve hizmet/paket ekleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
            {/* Business selection */}
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm">İşletme Seç (opsiyonel)</Label>
              <Select
                value={formData.business_id ? String(formData.business_id) : undefined}
                onValueChange={handleBusinessSelect}
              >
                <SelectTrigger className="text-xs sm:text-sm">
                  <SelectValue placeholder="İşletme seçin" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      İşletme bulunamadı
                    </SelectItem>
                  ) : (
                    businesses.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.title} {b.business_code ? `(${b.business_code})` : ''}
                      </SelectItem>
                    ))
                  )}
                  <SelectItem value="__clear__">Seçimi temizle</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Seçtiğiniz işletmenin ünvanı, yetkilisi, e-postası ve telefonu otomatik doldurulur.
              </p>
            </div>

            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="company_name" className="text-xs sm:text-sm">Şirket Adı *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Şirket Adı"
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="contact_name" className="text-xs sm:text-sm">İletişim Kişisi *</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="İletişim Kişisi"
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs sm:text-sm">E-posta *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs sm:text-sm">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+90 (XXX) XXX XX XX"
                  className="text-xs sm:text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address" className="text-xs sm:text-sm">Adres</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Adres"
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="tax_number" className="text-xs sm:text-sm">Vergi Numarası</Label>
                <Input
                  id="tax_number"
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  placeholder="Vergi no"
                  className="text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Offer Title */}
            <div>
              <Label htmlFor="offer_title" className="text-xs sm:text-sm">Teklif Başlığı *</Label>
              <Input
                id="offer_title"
                value={formData.offer_title}
                onChange={(e) => setFormData({ ...formData, offer_title: e.target.value })}
                placeholder="Örn: Kurumsal Web Sitesi Teklifi"
                className="text-xs sm:text-sm"
              />
            </div>

            {/* Services */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                <Label className="text-base sm:text-lg font-semibold">Hizmetler</Label>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowServiceSelect(true)}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    <Package className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Mevcut Hizmet Ekle
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addService} className="w-full sm:w-auto text-xs sm:text-sm">
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Yeni Hizmet Ekle
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {Array.isArray(formData.offer_data?.services) && formData.offer_data.services.map((service) => (
                  <Card key={service.id}>
                    <CardContent className="p-3 sm:p-4">
                      {service.original_service_id && (
                        <Badge variant="outline" className="mb-2 text-[10px] sm:text-xs">
                          Veritabanından: ID {service.original_service_id}
                        </Badge>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                        <div className="md:col-span-5">
                          <Label className="text-xs sm:text-sm">Hizmet Adı</Label>
                          <Input
                            value={service.name}
                            onChange={(e) => updateService(service.id, 'name', e.target.value)}
                            placeholder="Hizmet adı"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="md:col-span-4">
                          <Label className="text-xs sm:text-sm">Açıklama</Label>
                          <Input
                            value={service.description}
                            onChange={(e) => updateService(service.id, 'description', e.target.value)}
                            placeholder="Açıklama"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs sm:text-sm">Miktar</Label>
                          <Input
                            type="number"
                            value={service.quantity}
                            onChange={(e) => updateService(service.id, 'quantity', e.target.value)}
                            min="1"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs sm:text-sm">Birim Fiyat (₺)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={service.unit_price}
                            onChange={(e) => updateService(service.id, 'unit_price', e.target.value)}
                            min="0"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs sm:text-sm">Toplam (₺)</Label>
                          <Input
                            type="number"
                            value={parseFloat(service.total).toFixed(2)}
                            disabled
                            className="bg-gray-50 text-xs sm:text-sm"
                          />
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeService(service.id)}
                            className="text-red-600 h-8 w-8 sm:h-10 sm:w-10 p-0"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom Packages */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                <Label className="text-base sm:text-lg font-semibold">Özel Paketler</Label>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowPackageSelect(true)}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    <Package className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Mevcut Paket Ekle
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addCustomPackage} className="w-full sm:w-auto text-xs sm:text-sm">
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Yeni Paket Ekle
                  </Button>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {Array.isArray(formData.offer_data?.custom_packages) && formData.offer_data.custom_packages.map((pkg) => (
                  <Card key={pkg.id}>
                    <CardContent className="p-3 sm:p-4">
                      {pkg.original_package_id && (
                        <Badge variant="outline" className="mb-2 text-[10px] sm:text-xs">
                          Veritabanından: {pkg.package_type} - ID {pkg.original_package_id}
                        </Badge>
                      )}
                      <div className="space-y-3 sm:space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
                          <div className="md:col-span-3">
                            <Label className="text-xs sm:text-sm">Paket Adı</Label>
                            <Input
                              value={pkg.name}
                              onChange={(e) => updateCustomPackage(pkg.id, 'name', e.target.value)}
                              placeholder="Paket adı"
                              className="text-xs sm:text-sm"
                            />
                          </div>
                          <div className="md:col-span-4">
                            <Label className="text-xs sm:text-sm">Açıklama</Label>
                            <Input
                              value={pkg.description}
                              onChange={(e) => updateCustomPackage(pkg.id, 'description', e.target.value)}
                              placeholder="Açıklama"
                              className="text-xs sm:text-sm"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs sm:text-sm">Mevcut Paketten Türet</Label>
                            <Select
                              value={pkg.based_on_package_id || 'none'}
                              onValueChange={(value) => updateCustomPackage(pkg.id, 'based_on_package_id', value === 'none' ? null : value)}
                            >
                              <SelectTrigger className="text-xs sm:text-sm">
                                <SelectValue placeholder="Seçiniz" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Yok</SelectItem>
                                {Array.isArray(packages) && packages.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} ({p.type})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs sm:text-sm">Fiyat (₺)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={pkg.price}
                              onChange={(e) => updateCustomPackage(pkg.id, 'price', e.target.value)}
                              min="0"
                              className="text-xs sm:text-sm"
                            />
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCustomPackage(pkg.id)}
                              className="text-red-600 h-8 w-8 sm:h-10 sm:w-10 p-0"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Features Section */}
                        <div className="border-t pt-3 sm:pt-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-2">
                            <Label className="text-xs sm:text-sm font-semibold">Özellikler</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addPackageFeature(pkg.id)}
                              className="w-full sm:w-auto text-xs sm:text-sm"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Özellik Ekle
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                              pkg.features.map((feature, featureIndex) => (
                                <div key={featureIndex} className="flex items-center gap-2">
                                  <Input
                                    value={feature}
                                    onChange={(e) => updatePackageFeature(pkg.id, featureIndex, e.target.value)}
                                    placeholder="Özellik adı"
                                    className="flex-1 text-xs sm:text-sm"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removePackageFeature(pkg.id, featureIndex)}
                                    className="text-red-600 h-8 w-8 sm:h-10 sm:w-10 p-0"
                                  >
                                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                  </Button>
                                </div>
                              ))
                            ) : (
                              <p className="text-xs sm:text-sm text-gray-500">Henüz özellik eklenmedi</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Total Amount (KDV dahil) */}
            {(() => {
              const totals = computeTotals();
              return (
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Ara Toplam (KDV hariç)</span>
                    <span className="text-sm sm:text-lg font-semibold">
                      {totals.subtotal.toFixed(2)} ₺
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">KDV (%{(VAT_RATE * 100).toFixed(0)})</span>
                    <span className="text-sm sm:text-lg font-semibold">
                      {totals.vatAmount.toFixed(2)} ₺
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                    <span className="text-sm sm:text-lg font-semibold">Genel Toplam (KDV dahil)</span>
                    <span className="text-lg sm:text-2xl font-bold text-blue-600">
                      {totals.total.toFixed(2)} ₺
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-xs sm:text-sm">Notlar</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Ek notlar..."
                rows={4}
                className="text-xs sm:text-sm"
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status" className="text-xs sm:text-sm">Durum</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Taslak</SelectItem>
                  <SelectItem value="sent">Gönderildi</SelectItem>
                  <SelectItem value="accepted">Kabul Edildi</SelectItem>
                  <SelectItem value="rejected">Reddedildi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-2 sm:pt-0">
            <Button variant="outline" onClick={closeModal} className="w-full sm:w-auto text-xs sm:text-sm">
              İptal
            </Button>
            <Button onClick={handleSave} className="w-full sm:w-auto text-xs sm:text-sm">
              <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Selection Dialog */}
      <Dialog open={showServiceSelect} onOpenChange={setShowServiceSelect}>
        <DialogContent className="max-w-md sm:max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="p-0 sm:p-0">
            <DialogTitle className="text-base sm:text-lg">Mevcut Hizmet Seç</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Veritabanından bir hizmet seçin. Fiyat ve açıklamayı teklif için özelleştirebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {services.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-xs sm:text-sm">Henüz hizmet bulunmuyor</p>
            ) : (
              <div className="space-y-2">
                {services.map((service) => (
                  <Card 
                    key={service.id} 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => addServiceFromDatabase(service.id)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm sm:text-base truncate">{service.name}</h4>
                          {service.description && (
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                              {service.description}
                            </p>
                          )}
                        </div>
                        {service.price && (
                          <div className="text-left sm:text-right flex-shrink-0">
                            <span className="font-semibold text-blue-600 text-sm sm:text-base">
                              {parseFloat(service.price).toFixed(2)} ₺
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 sm:pt-0">
            <Button variant="outline" onClick={() => setShowServiceSelect(false)} className="w-full sm:w-auto text-xs sm:text-sm">
              İptal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Selection Dialog */}
      <Dialog open={showPackageSelect} onOpenChange={setShowPackageSelect}>
        <DialogContent className="max-w-md sm:max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="p-0 sm:p-0">
            <DialogTitle className="text-base sm:text-lg">Mevcut Paket Seç</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Veritabanından bir paket seçin. Fiyat ve açıklamayı teklif için özelleştirebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {packages.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-xs sm:text-sm">Henüz paket bulunmuyor</p>
            ) : (
              <div className="space-y-2">
                {packages.map((pkg) => (
                  <Card 
                    key={pkg.id} 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => addPackageFromDatabase(pkg.id)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm sm:text-base truncate">{pkg.name}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Tip: {pkg.type}
                          </p>
                        </div>
                        {pkg.price && (
                          <div className="text-left sm:text-right flex-shrink-0">
                            <span className="font-semibold text-blue-600 text-sm sm:text-base">
                              {parseFloat(pkg.price).toFixed(2)} ₺
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 sm:pt-0">
            <Button variant="outline" onClick={() => setShowPackageSelect(false)} className="w-full sm:w-auto text-xs sm:text-sm">
              İptal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOfferRequests;

